/**
 * Morning cockpit (#1004) — replaces the old overview projection at
 * `/business/overview`. One page answering the operator's daily loop:
 * yesterday/today true margin (the finance PnL numbers verbatim — same
 * source as the 真实盈亏 page), net cash in/out, top-5 upstream prepaid
 * runway, the top-10 customer chase list, open fires from the unified
 * risk aggregation, and the compact growth funnel with real conversion
 * rates (#999).
 *
 * First page to honor the #1001 required-to-render envelope contract:
 * the window bar renders `from`/`to`, the comparison period, the
 * `UTC+8` business timezone label, and `generatedAt` staleness. All CNY
 * figures are server-provided (`fx` converted once, upstream) — this
 * page never converts currencies client-side.
 *
 * Section-level degradation (#1002): every `null` section renders a
 * `SectionUnavailable` placeholder instead of blanking the page.
 */
import type {
  BusinessBrandFilter,
  BusinessCockpitCashWindow,
  BusinessCockpitFunnel,
  BusinessCockpitMarginWindow,
  BusinessCockpitResponse,
  BusinessMetric,
  BusinessWindowEnvelope,
  BusinessWindowPreset,
  FinancePnlDataQualityStatus,
} from '@nadoutong/contracts';
import {
  Calendar,
  CalendarDays,
  CalendarRange,
  Clock,
  Flame,
  SlidersHorizontal,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useBusinessPage } from '../api/business.ts';
import { formatCnyCents, formatUsdCents } from '../components/formatters.ts';
import { isStale } from '../components/freshness.ts';
import { RiskItemCard, riskDimensionFromArea } from '../components/risk.tsx';
import { useT, type I18nKey } from '../i18n/index.ts';
import { Button, Chip, PageHeader, Panel, Select, TBody, Table, Td, Th, THead, Tr } from '../ui/index.ts';
import type { ChipTone } from '../ui/index.ts';

const PCT_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 1,
});
const DAYS_FORMATTER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

const BUSINESS_TZ_OFFSET_MS = 8 * 60 * 60 * 1000;

export function CockpitPage(): JSX.Element {
  const controls = useCockpitControls();
  const query = useBusinessPage('cockpit', controls.query);
  const t = useT();
  return (
    <section className="flex min-w-0 flex-col gap-6" data-testid="business-cockpit-page">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <p className="m-0 text-label font-medium text-signal">{t('business.kicker')}</p>
          <PageHeader className="mt-1.5" title={t('cockpit.title')} />
          <p className="m-0 mt-2 max-w-3xl text-dense text-paper-low">{t('cockpit.subtitle')}</p>
        </div>
        <CockpitFilters controls={controls} />
      </header>
      {query.isLoading ? (
        <Panel caption={t('business.loading.title')} padded>
          <EmptyLine label={t('business.loading.body')} />
        </Panel>
      ) : query.isError ? (
        <div
          className="rounded-lg border border-bad-line bg-bad-dim p-5 text-body text-bad-bright"
          data-testid="business-error"
        >
          {t('business.error.prefix')} {query.error instanceof Error ? query.error.message : 'unknown error'}
        </div>
      ) : query.data === undefined ? (
        <Panel caption={t('business.empty.title')} padded>
          <EmptyLine label={t('business.empty.body')} />
        </Panel>
      ) : (
        <CockpitBody data={query.data} />
      )}
    </section>
  );
}

function CockpitBody({ data }: { readonly data: BusinessCockpitResponse }): JSX.Element {
  const t = useT();
  const labels = windowLabels(data.window.preset);
  return (
    <>
      <EnvelopeBar window={data.window} generatedAt={data.generatedAt} />
      <div className="grid gap-5 xl:grid-cols-2">
        {data.margin !== null ? (
          <Panel
            caption={t('cockpit.margin.title')}
            padded
            testId="cockpit-margin"
            actions={<SectionLink to="/business/metrics?tab=pnl" label={t('cockpit.margin.link')} />}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <MarginCard label={t(labels.previous)} window={data.margin.previous} />
              <MarginCard label={t(labels.current)} window={data.margin.current} />
            </div>
          </Panel>
        ) : (
          <SectionUnavailable section="margin" caption={t('cockpit.margin.title')} />
        )}
        {data.cash !== null ? (
          <Panel
            caption={t('cockpit.cash.title')}
            padded
            testId="cockpit-cash"
            actions={<SectionLink to="/business/metrics?tab=pnl" label={t('cockpit.margin.link')} />}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <CashCard label={t(labels.previous)} window={data.cash.previous} />
              <CashCard label={t(labels.current)} window={data.cash.current} />
            </div>
          </Panel>
        ) : (
          <SectionUnavailable section="cash" caption={t('cockpit.cash.title')} />
        )}
      </div>
      <div className="grid gap-5 xl:grid-cols-2">
        {data.upstreamRunway !== null ? (
          <Panel
            caption={t('cockpit.upstream.title')}
            testId="cockpit-upstream-runway"
            actions={<SectionLink to="/supply/upstreams" label={t('cockpit.upstream.link')} />}
          >
            <div className="flex flex-col">
              <p className="m-0 px-4 pt-3 text-label text-paper-low">
                {t('cockpit.runway.burn_window', { days: data.upstreamRunway.burnWindowDays })} ·{' '}
                {t('cockpit.upstream.total')}{' '}
                <span className="font-mono text-paper">
                  {formatUsdCents(data.upstreamRunway.totalRemainingUsdCents, false)}
                </span>{' '}
                <span className="font-mono text-paper-low">
                  ≈ {formatCnyCents(data.upstreamRunway.totalRemainingCnyCents, false)}
                </span>
              </p>
              {/* Capacity the allocator can no longer reach, shown only when
                  it exists so a healthy book stays uncluttered (#1513). */}
              {data.upstreamRunway.totalStrandedUsdCents > 0 ? (
                <p className="m-0 px-4 pt-1 text-micro text-paper-low" data-testid="cockpit-upstream-stranded">
                  {t('cockpit.upstream.stranded')}{' '}
                  <span className="font-mono">
                    {formatUsdCents(data.upstreamRunway.totalStrandedUsdCents, false)}
                  </span>{' '}
                  <span className="font-mono">
                    ≈ {formatCnyCents(data.upstreamRunway.totalStrandedCnyCents, false)}
                  </span>
                </p>
              ) : null}
              {data.upstreamRunway.rows.length === 0 ? (
                <div className="px-4 py-4">
                  <EmptyLine label={t('cockpit.upstream.empty')} />
                </div>
              ) : (
                <Table className="border-0 bg-transparent">
                  <THead>
                    <Tr interactive={false}>
                      <Th>{t('cockpit.table.upstream')}</Th>
                      <Th num>{t('cockpit.table.days_left')}</Th>
                      <Th num>{t('cockpit.table.remaining')}</Th>
                      <Th num>{t('cockpit.table.burn_per_day')}</Th>
                    </Tr>
                  </THead>
                  <TBody>
                    {data.upstreamRunway.rows.map((row) => (
                      <Tr key={row.upstreamId} data-testid="cockpit-upstream-row">
                        <Td emphasis>
                          <Link to={row.href} className="text-signal hover:underline">
                            {row.upstreamName.length > 0 ? row.upstreamName : row.upstreamId}
                          </Link>
                          <div className="font-mono text-micro font-normal text-paper-low">{row.upstreamId}</div>
                        </Td>
                        <Td num>
                          <DaysLeftCell daysLeft={row.daysLeft} />
                        </Td>
                        <Td num>
                          <div>{formatUsdCents(row.remainingUsdCents, false)}</div>
                          <div className="text-micro text-paper-low">≈ {formatCnyCents(row.remainingCnyCents, false)}</div>
                        </Td>
                        <Td num>{formatUsdCents(row.burnPerDayUsdCents, false)}</Td>
                      </Tr>
                    ))}
                  </TBody>
                </Table>
              )}
            </div>
          </Panel>
        ) : (
          <SectionUnavailable section="upstreamRunway" caption={t('cockpit.upstream.title')} />
        )}
        {data.customerRunway !== null ? (
          <Panel
            caption={t('cockpit.chase.title')}
            testId="cockpit-customer-runway"
            actions={
              <span className="inline-flex items-center gap-3">
                {/* #1013: deep link into the URL-backed low-balance view.
                    Different population than this chase list on purpose:
                    the chase list ranks by burn-based days-left, the
                    outlink filters by point-in-time EFFECTIVE balance
                    (balance + own credit headroom) <= 0 — credit-aware since
                    epic #1033 E4-C3/C8, so a credit customer with headroom is
                    not surfaced. */}
                <SectionLink
                  to="/customers/accounts?lowBalance=true&sort=balance"
                  label={t('cockpit.chase.low_balance_link')}
                />
                <SectionLink to="/customers/accounts" label={t('cockpit.chase.link')} />
              </span>
            }
          >
            <div className="flex flex-col">
              <p className="m-0 px-4 pt-3 text-label text-paper-low">
                {t('cockpit.runway.burn_window', { days: data.customerRunway.burnWindowDays })} ·{' '}
                {t('cockpit.chase.hint')}
              </p>
              {data.customerRunway.rows.length === 0 ? (
                <div className="px-4 py-4">
                  <EmptyLine label={t('cockpit.chase.empty')} />
                </div>
              ) : (
                <Table className="border-0 bg-transparent">
                  <THead>
                    <Tr interactive={false}>
                      <Th>{t('cockpit.table.customer')}</Th>
                      <Th num>{t('cockpit.table.days_left')}</Th>
                      <Th num>{t('cockpit.table.remaining')}</Th>
                      <Th num>{t('cockpit.table.burn_per_day')}</Th>
                    </Tr>
                  </THead>
                  <TBody>
                    {data.customerRunway.rows.map((row) => (
                      <Tr key={row.accountId} data-testid="cockpit-chase-row">
                        <Td emphasis>
                          <Link to={row.href} className="text-signal hover:underline">
                            {row.accountEmail}
                          </Link>
                          <div className="font-mono text-micro font-normal text-paper-low">{row.accountId}</div>
                        </Td>
                        <Td num>
                          <DaysLeftCell daysLeft={row.daysLeft} />
                        </Td>
                        <Td num>
                          <div>{formatUsdCents(row.remainingUsdCents, false)}</div>
                          <div className="text-micro text-paper-low">≈ {formatCnyCents(row.remainingCnyCents, false)}</div>
                        </Td>
                        <Td num>{formatUsdCents(row.burnPerDayUsdCents, false)}</Td>
                      </Tr>
                    ))}
                  </TBody>
                </Table>
              )}
            </div>
          </Panel>
        ) : (
          <SectionUnavailable section="customerRunway" caption={t('cockpit.chase.title')} />
        )}
      </div>
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
        {data.fires !== null ? (
          <Panel
            caption={
              <span className="inline-flex items-center gap-2">
                <Flame aria-hidden="true" className="h-4 w-4 text-warn" />
                {t('cockpit.fires.title')}
              </span>
            }
            count={data.fires.openCount}
            padded
            testId="cockpit-fires"
            actions={<SectionLink to="/business/risk" label={t('cockpit.fires.link')} />}
          >
            {data.fires.items.length === 0 ? (
              <EmptyLine label={t('business.empty.no_risks')} />
            ) : (
              <div className="flex flex-col gap-3">
                {data.fires.items.length < data.fires.openCount ? (
                  <p className="m-0 text-label text-paper-low">
                    {t('cockpit.fires.showing', {
                      shown: data.fires.items.length,
                      total: data.fires.openCount,
                    })}
                  </p>
                ) : null}
                {data.fires.items.map((item) => (
                  <RiskItemCard
                    key={item.id}
                    testId="cockpit-fire-item"
                    item={{
                      id: item.id,
                      dimension: riskDimensionFromArea(item.area),
                      dimensionFallback: item.area,
                      severity: item.severity,
                      title: item.title,
                      detail: item.detail,
                      targetLabel: item.targetLabel,
                      href: item.href,
                      occurredAt: item.occurredAt,
                    }}
                  />
                ))}
              </div>
            )}
          </Panel>
        ) : (
          <SectionUnavailable section="fires" caption={t('cockpit.fires.title')} />
        )}
        {data.funnel !== null ? (
          <Panel
            caption={t('cockpit.funnel.title')}
            padded
            testId="cockpit-funnel"
            // #1008: `/business/customers` now redirects back to this cockpit
            // (a drill-through dead-end after #1007); point the funnel at the
            // real customer surface — the account list.
            actions={<SectionLink to="/customers/accounts" label={t('cockpit.funnel.link')} />}
          >
            <FunnelSection funnel={data.funnel} />
          </Panel>
        ) : (
          <SectionUnavailable section="funnel" caption={t('cockpit.funnel.title')} />
        )}
      </div>
    </>
  );
}

// ─── Envelope (#1001 required-to-render) ─────────────────────────────

function EnvelopeBar({
  window: envelope,
  generatedAt,
}: {
  readonly window: BusinessWindowEnvelope;
  readonly generatedAt: string;
}): JSX.Element {
  const t = useT();
  const stale = isStale(generatedAt);
  return (
    <div
      className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-line-soft bg-card px-4 py-3"
      data-testid="cockpit-envelope"
    >
      <EnvelopeCell
        label={t('cockpit.envelope.window')}
        value={`${formatBusinessTime(envelope.from)} → ${formatBusinessTime(envelope.to)}`}
      />
      <EnvelopeCell
        label={t('cockpit.envelope.compare')}
        value={`${formatBusinessTime(envelope.previousFrom)} → ${formatBusinessTime(envelope.previousTo)}`}
      />
      <Chip tone="signal">{envelope.timezone}</Chip>
      <span className="ml-auto inline-flex items-center gap-1.5" data-testid="cockpit-generated-at">
        <Clock aria-hidden="true" className={`h-3.5 w-3.5 ${stale ? 'text-warn' : 'text-paper-low'}`} />
        <span className={`font-mono text-label ${stale ? 'text-warn' : 'text-paper-low'}`}>
          {t('cockpit.envelope.generated')} {formatBusinessTime(generatedAt)}
          {stale ? ` · ${t('cockpit.envelope.stale')}` : ''}
        </span>
      </span>
    </div>
  );
}

function EnvelopeCell({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <span className="inline-flex min-w-0 items-baseline gap-2">
      <span className="text-label text-paper-low">{label}</span>
      <span className="font-mono text-label text-paper">{value}</span>
    </span>
  );
}

/**
 * Render a UTC instant as a `UTC+8` wall-clock time — the envelope's
 * declared business timezone (#1001) — instead of the device timezone,
 * so the bar reads identically for every operator.
 */
function formatBusinessTime(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  const d = new Date(ms + BUSINESS_TZ_OFFSET_MS);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

// ─── Margin / cash ───────────────────────────────────────────────────

const QUALITY_TONE: Readonly<Record<FinancePnlDataQualityStatus, ChipTone>> = {
  balanced: 'ok',
  partial: 'warn',
  unreliable: 'bad',
};

const QUALITY_KEY: Readonly<Record<FinancePnlDataQualityStatus, I18nKey>> = {
  balanced: 'cockpit.quality.balanced',
  partial: 'cockpit.quality.partial',
  unreliable: 'cockpit.quality.unreliable',
};

function MarginCard({
  label,
  window: pnl,
}: {
  readonly label: string;
  readonly window: BusinessCockpitMarginWindow;
}): JSX.Element {
  const t = useT();
  return (
    <div className="rounded-lg bg-well px-4 py-3" data-testid="cockpit-margin-card">
      <div className="flex items-center justify-between gap-2">
        <span className="text-label text-paper-low">{label}</span>
        <Chip tone={QUALITY_TONE[pnl.dataQuality]}>{t(QUALITY_KEY[pnl.dataQuality])}</Chip>
      </div>
      <p className={`m-0 mt-2 font-mono text-stat ${pnl.profitCnyCents < 0 ? 'text-bad-bright' : 'text-paper'}`}>
        {formatCnyCents(pnl.profitCnyCents, true)}
      </p>
      <p className="m-0 mt-1 text-label text-paper-low">
        {t('cockpit.margin.margin')} {PCT_FORMATTER.format(pnl.marginPct)}
      </p>
      <dl className="m-0 mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <BreakdownRow label={t('cockpit.margin.revenue')} value={formatCnyCents(pnl.revenueCnyCents, false)} />
        <BreakdownRow label={t('cockpit.margin.cost')} value={formatCnyCents(-pnl.upstreamCostCnyCents, true)} />
        <BreakdownRow label={t('cockpit.margin.gifted')} value={formatCnyCents(-pnl.giftedCostCnyCents, true)} />
      </dl>
    </div>
  );
}

function CashCard({
  label,
  window: cash,
}: {
  readonly label: string;
  readonly window: BusinessCockpitCashWindow;
}): JSX.Element {
  const t = useT();
  return (
    <div className="rounded-lg bg-well px-4 py-3" data-testid="cockpit-cash-card">
      <span className="text-label text-paper-low">{label}</span>
      <p className={`m-0 mt-2 font-mono text-stat ${cash.netCnyCents < 0 ? 'text-bad-bright' : 'text-paper'}`}>
        {formatCnyCents(cash.netCnyCents, true)}
      </p>
      <p className="m-0 mt-1 text-label text-paper-low">{t('cockpit.cash.net')}</p>
      <dl className="m-0 mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <BreakdownRow label={t('cockpit.cash.in')} value={formatCnyCents(cash.cashInCnyCents, true)} />
        <BreakdownRow label={t('cockpit.cash.fees')} value={formatCnyCents(-cash.paymentFeeCnyCents, true)} />
        <BreakdownRow label={t('cockpit.cash.out')} value={formatCnyCents(-cash.upstreamCashOutCnyCents, true)} />
      </dl>
    </div>
  );
}

function BreakdownRow({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <>
      <dt className="text-label text-paper-low">{label}</dt>
      <dd className="m-0 text-right font-mono text-label text-paper-mid">{value}</dd>
    </>
  );
}

// ─── Runway cells ────────────────────────────────────────────────────

function DaysLeftCell({ daysLeft }: { readonly daysLeft: number | null }): JSX.Element {
  const t = useT();
  if (daysLeft === null) {
    return <span className="text-paper-low">{t('cockpit.runway.no_burn')}</span>;
  }
  const toneClass = daysLeft < 3 ? 'text-bad-bright' : daysLeft < 7 ? 'text-warn' : 'text-paper';
  return (
    <span className={`font-semibold ${toneClass}`}>
      {t('cockpit.runway.days', { days: DAYS_FORMATTER.format(daysLeft) })}
    </span>
  );
}

// ─── Funnel (#999 — two populations, labeled distinctly) ─────────────

function FunnelSection({ funnel }: { readonly funnel: BusinessCockpitFunnel }): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-4">
      <div data-testid="cockpit-funnel-cohort">
        <p className="m-0 text-label font-medium text-paper-mid">{t('cockpit.funnel.cohort')}</p>
        <div className="mt-2 flex flex-col gap-1.5">
          <FunnelStage labelKey="business.metric.new_accounts" metric={funnel.cohort.newAccounts} rate={null} />
          <FunnelStage
            labelKey="business.metric.verified_accounts"
            metric={funnel.cohort.verifiedAccounts}
            rate={funnel.cohort.verifiedRate}
          />
          <FunnelStage
            labelKey="business.metric.keyed_accounts"
            metric={funnel.cohort.keyedAccounts}
            rate={funnel.cohort.keyedRate}
          />
        </div>
      </div>
      <div data-testid="cockpit-funnel-activity">
        <p className="m-0 text-label font-medium text-paper-mid">{t('cockpit.funnel.activity')}</p>
        <div className="mt-2 flex flex-col gap-1.5">
          <FunnelStage
            labelKey="business.metric.active_customers"
            metric={funnel.activity.activeCustomers}
            rate={null}
          />
          <FunnelStage
            labelKey="business.metric.paying_customers"
            metric={funnel.activity.payingCustomers}
            rate={funnel.activity.payingRate}
          />
        </div>
      </div>
      <p className="m-0 text-micro text-paper-low">{t('cockpit.funnel.caveat')}</p>
    </div>
  );
}

function FunnelStage({
  labelKey,
  metric,
  rate,
}: {
  readonly labelKey: I18nKey;
  readonly metric: BusinessMetric;
  readonly rate: number | null;
}): JSX.Element {
  const t = useT();
  return (
    <div className="flex items-center justify-between gap-3 rounded-md bg-well px-3 py-2">
      <span className="min-w-0 truncate text-dense text-paper-mid">{t(labelKey)}</span>
      <span className="flex shrink-0 items-baseline gap-2">
        {rate !== null ? (
          <span className="font-mono text-micro text-signal" data-testid="cockpit-funnel-rate">
            {t('cockpit.funnel.conversion', { pct: PCT_FORMATTER.format(rate) })}
          </span>
        ) : null}
        <span className="font-mono text-dense font-semibold text-paper">{metric.value}</span>
      </span>
    </div>
  );
}

// ─── Controls / shared chrome ────────────────────────────────────────

interface CockpitControls {
  readonly query: {
    readonly window: BusinessWindowPreset;
    readonly brand: BusinessBrandFilter;
  };
  readonly setWindow: (next: BusinessWindowPreset) => void;
  readonly setBrand: (next: BusinessBrandFilter) => void;
}

/** URL-backed window/brand controls. The cockpit offers the preset
 * windows only (no custom bounds) — its daily loop defaults to `today`,
 * where the comparison window is exactly yesterday. */
function useCockpitControls(): CockpitControls {
  const [params, setParams] = useSearchParams();
  const preset = parseWindow(params.get('window'));
  const brand = parseBrand(params.get('brand'));

  function setWindow(next: BusinessWindowPreset): void {
    const clone = new URLSearchParams(params);
    clone.set('window', next);
    setParams(clone, { replace: true });
  }

  function setBrand(next: BusinessBrandFilter): void {
    const clone = new URLSearchParams(params);
    if (next === 'all') clone.delete('brand');
    else clone.set('brand', next);
    setParams(clone, { replace: true });
  }

  return { query: { window: preset, brand }, setWindow, setBrand };
}

function CockpitFilters({ controls }: { readonly controls: CockpitControls }): JSX.Element {
  const t = useT();
  return (
    <div
      className="flex max-w-full flex-wrap items-end gap-3 rounded-lg border border-line-soft bg-card px-3 py-3"
      role="group"
      aria-label={t('business.filters.command_bar')}
    >
      <div className="flex items-center gap-2">
        <SlidersHorizontal aria-hidden="true" className="h-4 w-4 shrink-0 text-signal" />
        <div className="flex flex-wrap gap-1" role="tablist" aria-label={t('business.filters.window')}>
          {(['today', '7d', '30d'] as const).map((item) => {
            const active = controls.query.window === item;
            const Icon = windowIcon(item);
            return (
              <Button
                key={item}
                role="tab"
                aria-selected={active}
                variant={active ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => controls.setWindow(item)}
                data-testid={`business-window-${item}`}
              >
                <Icon aria-hidden="true" className="h-3.5 w-3.5" />
                {item === 'today' ? t('business.window.today') : item}
              </Button>
            );
          })}
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="text-label text-paper-mid">{t('business.filters.brand')}</span>
        <Select
          value={controls.query.brand}
          onValueChange={(value) => controls.setBrand(parseBrand(value))}
          ariaLabel={t('business.filters.brand')}
          testId="business-brand-filter"
          className="w-40"
          options={[
            { value: 'all', label: t('business.brand.all') },
            { value: 'nadoutong', label: 'Nadoutong' },
            { value: 'nxtpath', label: 'Nxtpath' },
          ]}
        />
      </div>
    </div>
  );
}

function windowIcon(item: BusinessWindowPreset): LucideIcon {
  if (item === 'today') return Calendar;
  if (item === '7d') return CalendarRange;
  return CalendarDays;
}

function windowLabels(preset: BusinessWindowPreset): {
  readonly current: I18nKey;
  readonly previous: I18nKey;
} {
  if (preset === 'today') {
    return { current: 'cockpit.window.today', previous: 'cockpit.window.yesterday' };
  }
  return { current: 'cockpit.window.current', previous: 'cockpit.window.previous' };
}

function SectionLink({ to, label }: { readonly to: string; readonly label: string }): JSX.Element {
  return (
    <Link to={to} className="text-label text-signal hover:underline">
      {label}
    </Link>
  );
}

function SectionUnavailable({
  section,
  caption,
}: {
  readonly section: string;
  readonly caption: string;
}): JSX.Element {
  const t = useT();
  return (
    <div data-testid={`business-section-unavailable-${section}`}>
      <Panel caption={caption} padded>
        <EmptyLine label={t('business.section.unavailable')} />
      </Panel>
    </div>
  );
}

function EmptyLine({ label }: { readonly label: string }): JSX.Element {
  return <p className="m-0 text-dense text-paper-low">{label}</p>;
}

function parseWindow(raw: string | null): BusinessWindowPreset {
  return raw === '7d' || raw === '30d' ? raw : 'today';
}

function parseBrand(raw: string | null): BusinessBrandFilter {
  return raw === 'nadoutong' || raw === 'nxtpath' ? raw : 'all';
}
